import React, { useMemo } from 'react'

import * as Modal from '_components/modal/generic-modal'
import Button from '_/components/button'
import useStyles from './styles'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { BasicInput, Checkbox, ControlledSwitch, Select } from '_/components/inputs'
import HelpInfoButton from '_/components/button/help-info-button'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

const DEFAULT_VALUES = {
  name: '',
  enterpriseManagement: false,
  active: true,
  profileType: '',
  myProvidersSelection: false,
  providerAssociation: false,
  companyType: '',
}

const schema = yup.object({
  name: yup.string().required('Nome é obrigatório').max(50, 'Não deve exceder 50 caracteres'),
  enterpriseManagement: yup.boolean(),
  profileType: yup.string().required('Tipo de perfil é obrigatório'),
  myProvidersSelection: yup.boolean(),
  providerAssociation: yup.boolean(),
  active: yup.boolean(),
  companyType: yup.string().required('Tipo de empresa é obrigatório'),
  panelUrl: yup.string().notRequired(),
})

export function AreaOfActivityCRUDModal({
  open,
  loading,
  onClose,
  onSubmit,
  onDelete,
  item,
  meta,
}) {
  const styles = useStyles()

  const editing = useMemo(() => Boolean(item && Object.hasOwn(item, 'id')), [item])

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
  })

  const { checkUserPermission } = useRolePermission()

  const onSumbitButtonClick = event => {
    event.preventDefault()
    checkUserPermission({
      permission: PERMISSIONS.AREAS_OF_ACTIVITY,
      permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
      functionAccess: methods.handleSubmit(onSubmit),
    })
  }

  const onDeleteButtonClick = event => {
    event.preventDefault()
    checkUserPermission({
      permission: PERMISSIONS.AREAS_OF_ACTIVITY,
      permissionAction: PERMISSIONS_ACTIONS.DELETE,
      functionAccess: onDelete,
    })
  }

  return (
    <Modal.Root open={open} onClose={onClose} className={styles.root}>
      <Modal.TitleModal title="Área de Atuação" />
      <Modal.Content className={styles.dialogContent}>
        <FormProvider {...methods}>
          <form id="area-of-activity-form" className={styles.form} onSubmit={onSumbitButtonClick}>
            {/* Nome */}
            <div className={styles.row} style={{ gap: '16px' }}>
              <BasicInput
                label="Nome"
                name="name"
                defaultValue={item?.name ?? DEFAULT_VALUES.name}
                rules={{ required: true, maxLength: 50 }}
                style={{
                  flex: 1,
                }}
                required
                disabled
              />
              <BasicInput
                label="Tipo de empresa"
                name="companyType"
                defaultValue={item?.companyType ?? DEFAULT_VALUES.companyType}
                rules={{ required: true, maxLength: 100 }}
                style={{
                  flex: 2,
                }}
                required
                disabled
              />
            </div>
            {/* Tipo de perfil */}
            <Controller
              name="profileType"
              control={methods.control}
              defaultValue={item?.profileType ?? ''}
              render={field => (
                <Select
                  label="Tipo de perfil"
                  name={field.name}
                  options={meta?.profileType?.choices || []}
                  value={field.value || item?.profileType || ''}
                  defaultValue={item?.profileType || ''}
                  onChange={field.onChange}
                  required
                  helpInfoButton={{
                    title:
                      'Vincula a área de atuação ao tipo de contrato da intermediária, evitando erro de uso pela Refera.',
                    style: {
                      transform: 'scale(1.3)',
                      marginLeft: '8px',
                      marginBotton: '8px',
                      position: 'relative',
                      top: '-2px',
                    },
                  }}
                  error={!!field?.error}
                  helperText={field?.error?.message}
                  getValue={option => option.value || option}
                  getLabel={option => option.label || option}
                  getKey={option => option.value || option}
                  menuProps={{}}
                />
              )}
            />
            <div
              className={styles.row}
              style={{ marginBottom: '14px', display: 'flex', gap: '16px' }}
            >
              {/* Gestão de empreendimentos */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="enterpriseManagement"
                  label="Gestão de empreendimentos"
                  defaultValue={item?.enterpriseManagement ?? DEFAULT_VALUES.enterpriseManagement}
                />
                <HelpInfoButton
                  title="Se ativado, aparecerá uma nova opção 'Empreendimentos' no botão Dados adicionais do cadastro da Intermediárias, e um filtro de mesmo nome na lista de chamados."
                  placement="top"
                  style={{ marginTop: '-9px' }}
                />
              </fieldset>

              {/* Vínculo de prestadores */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="providerAssociation"
                  label="Vínculo de prestadores"
                  defaultValue={item?.providerAssociation ?? false}
                />
                <HelpInfoButton
                  title="Se assinalado, a Refera pode vincular prestadores às intermediárias."
                  placement="top"
                  style={{ marginTop: '-9px' }}
                />
              </fieldset>

              {/* Prestador por empreendimento */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="providerByEnterprise"
                  label="Prestador por empreendimento"
                  defaultValue={item?.providerByEnterprise ?? false}
                />
                <HelpInfoButton
                  title="Se assinalado, vai ser possível definir quais prestadores atendem cada um dos empreendimentos da intermediária. Em outras palavras, vai ser possível selecionar prestadores no cadastro de empreendimentos."
                  placement="top"
                  style={{ marginTop: '-9px' }}
                />
              </fieldset>
            </div>
            <div
              className={styles.row}
              style={{ marginBottom: '14px', display: 'flex', gap: '16px' }}
            >
              {/* Seleção de prestadores próprios */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="myProvidersSelection"
                  label="Seleção de prestadores próprios"
                  defaultValue={item?.myProvidersSelection ?? false}
                />
                <HelpInfoButton
                  title="Se assinalado, ao pedir para 'Selecionar prestadores' em um chamado, a plataforma primeiro vai trazer os vinculados à intermediária em questão, e só depois, os da Refera."
                  placement="top"
                  style={{ marginTop: '-9px' }}
                />
              </fieldset>
              {/* Chamados em imóveis vagos */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="manageUnoccupiedProperties"
                  label="Chamados em imóveis vagos"
                  defaultValue={item?.manageUnoccupiedProperties ?? false}
                />
                <HelpInfoButton
                  title="Se assinalado, permite que os colaboradores da intermediária abram chamados em imóveis vagos, tendo certas particularidades relacionadas à comunicação, avaliação e chaves."
                  placement="top"
                />
              </fieldset>
              {/* Gerencia aluguel de imóveis */}
              <fieldset className={styles.horizontalToggler}>
                <ControlledSwitch
                  name="managesPropertiesRentals"
                  label="Gerencia aluguel de imóveis"
                  defaultValue={item?.managesPropertiesRentals ?? false}
                />
                <HelpInfoButton
                  title="Se assinalado, mostrará dados relacionados a início e valor do contrato ativo de locação no formulário de abertura e outras áreas do sistema."
                  placement="top"
                />
              </fieldset>
            </div>
            {/* URL do painel */}
            <div className={styles.row}>
              <BasicInput
                label={
                  <div className={styles.labelWithHelpIcon}>
                    URL do painel
                    <HelpInfoButton
                      style={{ marginTop: '-9px' }}
                      title="Define a URL que será aberta quando acessado o menu principal “Painel”."
                      placement="bottom"
                    />
                  </div>
                }
                name="panelUrl"
                containerClasses={styles.panelUrlContainer}
                defaultValue={item?.panelUrl ?? null}
              />
            </div>
            {/* Ativo */}
            <div className={styles.row} style={{ marginTop: '-8px' }}>
              <Checkbox
                label="Ativo"
                name="active"
                defaultValue={item?.active ?? DEFAULT_VALUES.active}
              />
            </div>
          </form>
        </FormProvider>
      </Modal.Content>
      <Modal.Actions className={editing && styles.dynamicButtonContainer}>
        {editing && (
          <Button className={styles.link} onClick={onDeleteButtonClick} disabled={loading}>
            Excluir
          </Button>
        )}
        <Modal.Actions>
          <Modal.ButtonRed onClick={onClose} disabled={loading}>
            Cancelar
          </Modal.ButtonRed>
          <Modal.ButtonFullBlue type="submit" form="area-of-activity-form" disabled={loading}>
            Salvar
          </Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Actions>
    </Modal.Root>
  )
}
